/* sts-fbt.js — "Frequently bought together", the product-page bundle.
 *
 * Spec: docs/new-theme-v4/specs/04-collections-product-page-spec.md §7.2, §8.2,
 *       §9.8, criteria 50-53. Decisions 04-Q6, 04-Q29, 04-Q30, 05-Q32.
 * Markup: snippets/sts-fbt.liquid. Styles: assets/sts-fbt.css.
 *
 * ── The bundle and its discount (v6.2 §7) ────────────────────────────────────
 * Every line carries the hidden property `_bundle`: the products' handles,
 * sorted, joined with "+". Adding the same bundle again therefore lands on
 * the same lines (Shopify merges identical variant + properties + plan) and
 * raises their quantities. assets/sts-bundle.js breaks a bundle when one of
 * its products leaves the cart.
 * The 10% is a Shopify discount that reads `_bundle`; this file never sends a
 * code. The page claims a saving only when the theme setting sts_bundle_pct
 * says that discount is live (0, the default, claims nothing), or in the
 * staff simulator, which fakes 10% so the look can be reviewed. The bundle
 * price is each product less its own rounded percentage, the way a per-line
 * discount charges it. A successful add says nothing (v5.1 §1.5); the cart
 * shows what the CART charges.
 *
 * ── Where the products come from ─────────────────────────────────────────────
 * Typed with a routine: the routine's own products (one per category), priced
 * live from /products/{handle}.js, which answers in the buyer's market.
 * Otherwise, in order: the curated sts.fbt_products list, Shopify's
 * complementary set, the same-brand pool, then Shopify's related set -- the
 * two recommendation sets fetched as Liquid-rendered tiles, so every candidate
 * carries its approval metafields. One product per category, and a product
 * that is wrong for the shopper's type is never put in a bundle (04-Q30).
 *
 * Nothing here is sent to an ad pixel. sts_fbt_add carries source, item count,
 * value and currency only (decision 09-Q11).
 */
(function () {
  'use strict';

  /* -- Every request on this page has a deadline -------------------------------
     A fetch that hangs neither resolves nor rejects, so a `.catch()` alone is
     not a safety net: it only runs when the request FAILS, and a dead socket
     does not count as a failure. That is how "Add bundle" stayed aria-busy and disabled with nothing to click, and a hung
     product read left the row neither rendered nor hidden.
     A deadline turns a hang into the ordinary rejection each caller below was
     already written to handle.
     6000ms is a "the network is gone" stop, not a performance budget --
     aborting a slow-but-alive request would be worse than waiting for it. */
  var NET_TIMEOUT_MS = 6000;

  function netFetch(url, opts, ms) {
    var limit = ms || NET_TIMEOUT_MS;
    var o = {};
    var k;
    for (k in (opts || {})) { if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k]; }
    /* A caller that brought its own AbortController keeps it -- overwriting the
       signal would silently disarm an abort somebody else relies on. That call
       is capped by the race below instead. */
    var ctrl = (!o.signal && typeof window.AbortController === 'function')
      ? new window.AbortController() : null;
    if (ctrl) o.signal = ctrl.signal;
    var timer = window.setTimeout(function () {
      if (ctrl) { try { ctrl.abort(); } catch (e) { /* already gone */ } }
    }, limit);
    /* Plain window.fetch on purpose: shop_events_listener wraps it, and that
       is the only source of product_added_to_cart. See layout/theme.liquid. */
    var req = window.fetch(url, o);
    var race = ctrl ? req : Promise.race([req, new Promise(function (_, reject) {
      window.setTimeout(function () { reject(new Error('timeout')); }, limit);
    })]);
    return race.then(
      function (r) { window.clearTimeout(timer); return r; },
      function (e) {
        window.clearTimeout(timer);
        /* An abort otherwise reads as "Failed to fetch", which some callers put
           in front of the shopper. Say what actually happened. */
        throw (e && e.name === 'AbortError') ? new Error('timeout') : e;
      }
    );
  }

  /* One place decides which backend we talk to: snippets/sts-env.liquid
     resolves the sts.api_env / sts.*_base_* shop metafields and publishes
     window.STS.env. The value rendered into the data attribute stays as the
     fallback; going through env is also what applies the "dev never runs on
     the published theme" refusal. */
  function envBase(key, fallback, hard) {
    var E = window.STS && window.STS.env;
    return String((E && E.get) ? E.get(key, fallback || hard) : (fallback || hard));
  }

  var TYPES = ['DSPT', 'DSNT', 'DSPW', 'DSNW', 'OSPT', 'OSNT', 'OSPW', 'OSNW',
    'ORPT', 'ORNT', 'ORPW', 'ORNW', 'DRPT', 'DRNT', 'DRPW', 'DRNW'];
  /* [inside a sentence, at the start of one] */
  var COUNT = { 2: ['both', 'Both'], 3: ['all three', 'All three'], 4: ['all four', 'All four'] };
  var MONTH = 30 * 24 * 60 * 60 * 1000;
  var TOKEN_LIFE = 2 * 60 * 60 * 1000; /* the widget token's own lifetime */
  var NET_MS = 6000;                   /* a slow API never holds the row hostage */
  var MOST_TYPES = 9;                  /* "most skin types" = more than half of 16 */

  // ------------------------------------------------------------------ helpers

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function words(value) { return String(value || '').split(/\s+/).filter(Boolean); }
  function parse(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }
  function noop() {}

  /* Safari private mode throws on storage ACCESS, not only on write. */
  function stored(area, key) {
    try { return window[area].getItem(key); } catch (e) { return null; }
  }

  function state() {
    return (window.STS && window.STS.state) || { type: null, loggedIn: false };
  }

  function capped(promise, fallback) {
    var timer = new Promise(function (resolve) { setTimeout(function () { resolve(fallback); }, NET_MS); });
    return Promise.race([promise.catch(function () { return fallback; }), timer]);
  }

  function rootUrl(root) {
    var base = root.getAttribute('data-root') || '/';
    return base.charAt(base.length - 1) === '/' ? base : base + '/';
  }

  function cleanHandle(handle) {
    return String(handle || '').replace(/^\/+/, '').replace(/^products\//, '').split(/[?#]/)[0];
  }

  /* Only same-origin paths and https URLs ever become an href or a src. */
  function safeHref(url) {
    url = String(url || '');
    return /^(\/(?!\/)|https:\/\/)/.test(url) ? url : '';
  }
  function safeSrc(url) {
    url = String(url || '');
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return /^(\/(?!\/)|https:\/\/)/.test(url) ? url : '';
  }
  function sized(url, width) {
    url = safeSrc(url);
    if (!url || /[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function setText(el, value) {
    if (!el) return;
    if (value) el.textContent = value;
    else if (el.parentNode) el.parentNode.removeChild(el);
  }

  /* Every figure on this row is formatted here, from cents, in the cart's
     currency — the per-product prices and the total come from one formatter,
     so they can never be written two different ways side by side. */
  /* v6.1 13.3: the head's STS.money, the shop's format for the market. trim:
     a whole amount prints "$48", not "$48.00", as the grid cards do (at home;
     another market reads the code-named form, as those cards do too). */
  function moneyFormatter(root) {
    var currency = root.getAttribute('data-currency') || 'USD';
    var M = window.STS && window.STS.money;
    return function (cents) { return M ? M.cents(cents, { cur: currency, trim: true }) : (cents / 100).toFixed(2); };
  }

  // -------------------------------------------------------------------- items

  /* One reader for both shapes: this snippet's pool entries and the
     sts-pdp-card tiles the recommendations section returns. They share the
     sts-product-fit attribute contract; the pdp card keeps its image, brand and
     link in the markup rather than in attributes. */
  function readItem(node, source) {
    function attr(name) { return node.getAttribute(name) || ''; }
    var image = attr('data-image');
    var pic = node.querySelector('img');
    if (!image && pic) image = pic.getAttribute('src') || '';
    var vendor = attr('data-vendor');
    var brand = node.querySelector('.sts-tile__brand');
    if (!vendor && brand) vendor = brand.textContent.trim();
    var url = attr('data-url');
    var link = node.querySelector('a[href]');
    if (!url && link) url = link.getAttribute('href') || '';
    return {
      source: source || attr('data-source'),
      productId: attr('data-product-id'),
      variantId: attr('data-variant-id'),
      handle: attr('data-handle'),
      url: url,
      title: attr('data-title'),
      vendor: vendor,
      type: attr('data-type'),
      image: image,
      price: parseInt(attr('data-price'), 10),
      available: attr('data-available') === 'true',
      approved: words(attr('data-sts-approved')),
      sens: words(attr('data-sts-sens')),
      slots: words(attr('data-sts-slots')),
      serverFit: attr('data-sts-fit'),
      inRoutine: false
    };
  }

  /* v6.3 §5: a gift card is never in a bundle, as a candidate or as the
     anchor: it is one-time and never discounted. */
  function buyable(item) {
    return !!item && /^\d+$/.test(item.variantId) && item.available &&
      isFinite(item.price) && item.price >= 0 && !!item.title &&
      item.handle !== 'skin-type-gift-card' && !/^gift ?card$/i.test(item.type || '');
  }

  // --------------------------------------------------------------------- fit

  function wantedSubtypes() {
    var raw = stored('localStorage', 'customerSensitivityList');
    var list = parse(raw);
    if (!Array.isArray(list)) list = String(raw || '').split(',');
    return list.map(function (s) { return String(s).trim().toUpperCase(); })
      .filter(function (s) { return /^S[1-4]$/.test(s); });
  }

  /* The same verdict snippets/sts-product-fit.liquid reaches, for a shopper
     Liquid could not see (a typed guest). A missing sensitivity subtype counts
     as "not for your type" (04-Q7); R1 is not a subtype and is never required.
     `unrated` (no Baumann data at all: devices, supplements) is not `barred`. */
  function verdict(item, type, subtypes) {
    if (!type) return 'unknown';
    if (!item.approved.length) return 'unrated';
    if (item.approved.indexOf(type) < 0) return 'barred';
    if (item.serverFit === 'subtype') return 'subtype';
    for (var i = 0; i < subtypes.length; i++) {
      if (item.sens.indexOf(subtypes[i]) < 0) return 'subtype';
    }
    return 'approved';
  }

  function wrongForType(item, type, subtypes) {
    var v = verdict(item, type, subtypes);
    return v === 'barred' || v === 'subtype';
  }

  // ------------------------------------------------------------------ regimen

  /* The results page's saved copy. assets/sts-state.js wipes it on logout and
     on an account switch, and a copy built for a different type is refused, so
     it is never somebody else's routine or a pre-retake one. */
  function cachedSteps(type) {
    var saved = parse(stored('localStorage', 'slqSavedRegimen'));
    if (!saved || !Array.isArray(saved.steps) || !saved.steps.length) return null;
    if (!saved.cachedAt || Date.now() - saved.cachedAt > MONTH) return null;
    if (String(saved.skinType || '').toUpperCase() !== type) return null;
    return saved.steps;
  }

  function guestToken(type) {
    var sources = [
      parse(stored('sessionStorage', 'slqResultsHandoff')),
      parse(stored('localStorage', 'slqSavedResults'))
    ];
    for (var i = 0; i < sources.length; i++) {
      var s = sources[i];
      if (!s || !s.widgetToken) continue;
      if (s.skinType && String(s.skinType).toUpperCase() !== type) continue;
      if (s.savedAt && Date.now() - s.savedAt > TOKEN_LIFE) continue;
      return String(s.widgetToken);
    }
    return null;
  }

  function fetchSteps(ec2, token) {
    return netFetch(ec2 + '/widget/regimen', { headers: { Authorization: 'Bearer ' + token } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (payload) {
        return payload && Array.isArray(payload.steps) && payload.steps.length ? payload.steps : null;
      });
  }

  /* The staff simulator's routine fixture, when this session asked for one.
     The same call the four other routine surfaces make; this row was the one
     that did not, so ?sts_routine= left it composing from the pool and 9.8's
     first source could not be exercised on a preview theme at all.
     STS.previewRoutine() returns null on the published theme by two
     independent readings of theme.role: it cannot reach a shopper. */
  function previewSteps() {
    var fn = window.STS && window.STS.previewRoutine;
    if (typeof fn !== 'function') return Promise.resolve(null);
    return Promise.resolve(fn()).then(function (r) {
      return (r && Array.isArray(r.steps) && r.steps.length) ? r.steps : null;
    }).catch(function () { return null; });
  }

  /* Signed in: the live routine first (E1 then E2), the saved copy if that
     fails. A guest: the saved copy, then E2 with a still-valid widget token.
     Resolves to a step array or null, never rejects, never waits past NET_MS. */
  function loadRegimen(root, s) {
    return previewSteps().then(function (fixture) {
      return fixture || liveRegimen(root, s);
    });
  }

  function liveRegimen(root, s) {
    var ec2 = envBase('apiBase', root.getAttribute('data-ec2-base'), '').replace(/\/+$/, '');
    var proxy = envBase('proxyBase', root.getAttribute('data-proxy-base'), '/apps/quiz').replace(/\/+$/, '');
    var cached = cachedSteps(s.type);

    if (s.loggedIn) {
      var live = netFetch(proxy + '/my-regimen', { credentials: 'same-origin' })
        .then(function (r) { return (r.ok && r.status !== 204) ? r.json() : null; })
        .then(function (me) {
          if (!me || !me.widgetToken || !ec2) return null;
          if (me.skinType && String(me.skinType).toUpperCase() !== s.type) return null;
          return fetchSteps(ec2, me.widgetToken);
        });
      return capped(live, null).then(function (steps) { return steps || cached; });
    }
    if (cached) return Promise.resolve(cached);
    var token = guestToken(s.type);
    return token && ec2 ? capped(fetchSteps(ec2, token), null) : Promise.resolve(null);
  }

  /* Routine slot codes: HA… hypoallergenic, C cleanser, E eye, M moisturiser,
     S sunscreen, T treatment. Same prefixes as assets/sts-routine-shared.js. */
  function category(slot) {
    var s = String(slot || '').trim().toUpperCase();
    if (s.indexOf('HA') === 0) return 'HA';
    return /^[CEMST]/.test(s) ? s.charAt(0) : null;
  }

  /* The other steps, one per category, the anchor's own period first. When the
     anchor leads the bundle its categories are reserved, so the bundle never
     holds two cleansers. When it is barred and left out, they are not: the
     shopper's own cleanser is exactly what belongs beside a cleanser that is
     wrong for them. */
  function regimenCandidates(steps, anchor, anchorLeads) {
    var mine = anchor ? steps.filter(function (st) {
      return cleanHandle(st.handle) === anchor.handle ||
        (st.dtcVariantId != null && String(st.dtcVariantId) === anchor.variantId);
    }) : [];

    var taken = {};
    if (anchorLeads) {
      (mine.length ? mine.map(function (st) { return st.slot; }) : anchor.slots).forEach(function (slot) {
        var c = category(slot);
        if (c) taken[c] = true;
      });
    }

    var ordered = steps.slice();
    var tod = (window.STS && STS.routineTime) || String;
    var period = mine.length ? tod(mine[0].time) : null;
    if (period === 'AM' || period === 'PM') {
      var first = [];
      var rest = [];
      ordered.forEach(function (st) { var t = tod(st.time); (t === period || t === 'BOTH' ? first : rest).push(st); });
      ordered = first.concat(rest);
    }

    var seen = {};
    if (anchor) seen[anchor.handle] = true;
    var list = [];
    ordered.forEach(function (st) {
      var handle = cleanHandle(st.handle);
      var c = category(st.slot);
      if (!handle || st.dtcVariantId == null || !c || st.inStock === false) return;
      if (seen[handle] || taken[c]) return;
      seen[handle] = true;
      taken[c] = true;
      list.push({ handle: handle, variantId: String(st.dtcVariantId) });
    });
    return { inRegimen: mine.length > 0, list: list };
  }

  /* A routine product, priced and stocked live. The routine's own variant or
     nothing: switching a shopper to a different size is not our call (05-Q32).
     Approval comes from the 16 type tags, the store's mirror of
     product_info.baumann_skin_type_info (the list sts-product-fit and STP's own
     approval check read; equal on every rated product 2026-10-20); the routine engine picked these for this type anyway,
     so this is a second check, never the first. The S1-S4 tags are checked
     the same way: a product missing one of the shopper's sensitivity subtypes
     is dropped exactly as one barred for the type is (Robert, 6.3.2 note 03). */
  function regimenItem(root, pick, type, anchorType, subtypes) {
    return netFetch(rootUrl(root) + 'products/' + encodeURIComponent(pick.handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !Array.isArray(p.variants)) return null;
        var variant = null;
        p.variants.forEach(function (v) { if (String(v.id) === pick.variantId) variant = v; });
        if (!variant || !variant.available || typeof variant.price !== 'number') return null;
        if (anchorType && p.type && String(p.type).toLowerCase() === anchorType.toLowerCase()) return null;
        var tags = (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(','))
          .map(function (t) { return String(t).trim().toUpperCase(); });
        var approved = tags.filter(function (t) { return TYPES.indexOf(t) > -1; });
        if (approved.length && approved.indexOf(type) < 0) return null;
        var sens = tags.filter(function (t) { return /^S[1-4]$/.test(t); });
        if (approved.length && (subtypes || []).some(function (c) { return sens.indexOf(c) < 0; })) return null;
        /* v6.1 1.5b: the product's first image, never the variant's; the variant is price and cart only. */
        var image = p.featured_image || (variant.featured_image && variant.featured_image.src) || '';
        return {
          source: 'regimen',
          productId: String(p.id),
          variantId: String(variant.id),
          handle: p.handle,
          url: p.url || rootUrl(root) + 'products/' + p.handle,
          title: p.title,
          vendor: p.vendor || '',
          type: p.type || '',
          image: sized(image, 400),
          price: variant.price,
          available: true,
          approved: approved,
          sens: sens,
          slots: [],
          serverFit: '',
          inRoutine: true
        };
      })
      .catch(function () { return null; });
  }

  function fromRegimen(root, steps, anchor, anchorLeads, need, type, subtypes) {
    var cand = regimenCandidates(steps, anchor, anchorLeads);
    /* Two spares, so one sold-out product does not shrink the bundle. */
    var tries = cand.list.slice(0, need + 2);
    var anchorType = anchorLeads ? anchor.type : '';
    return Promise.all(tries.map(function (pick) { return regimenItem(root, pick, type, anchorType, subtypes); }))
      .then(function (found) {
        var others = found.filter(Boolean).slice(0, need);
        if (!others.length) return null;
        if (anchorLeads && cand.inRegimen) anchor.inRoutine = true;
        return {
          items: (anchorLeads ? [anchor] : []).concat(others),
          source: 'regimen',
          heading: (anchorLeads && cand.inRegimen ? 'The rest of your ' : 'Goes with your ') + type + ' routine'
        };
      });
  }

  // ---------------------------------------------- curated, S&D, brand, related

  /* One of Shopify's two recommendation sets, fetched THROUGH the section
     rendering API so each candidate arrives as a tile carrying its approval
     metafields; /recommendations/products.json cannot give those.

     `complementary` is curated by hand in Search & Discovery and, measured
     across this catalogue on 2026-09-19, returns ZERO products store-wide:
     9.8's third source is merchandising nobody has done yet. `related`, the
     algorithmic set, answers with real products on the same pages, so it is
     in the chain as the resilience source. It knows no Baumann types, which
     is why it is taken LAST, after the brand pool, and why its candidates go
     through take() below -- the same approval filter and one-per-category
     rule as every other source. It can widen a bundle; it can never put a
     product in one that the shopper's type bars. */
  function recommend(root, intent, limit) {
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId || typeof DOMParser === 'undefined') return Promise.resolve([]);
    var url = rootUrl(root) + 'recommendations/products?section_id=' + encodeURIComponent(sectionId) +
      '&product_id=' + encodeURIComponent(root.getAttribute('data-product-id')) +
      '&intent=' + intent + '&limit=' + limit;
    var req = netFetch(url)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return [];
        /* Parsed and READ, never inserted: no markup from the response reaches
           the page, only attribute values and text. */
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return qsa(doc, '[data-sts-tile], [data-sts-bundle-item]').map(function (node) {
          return readItem(node, intent);
        });
      });
    /* An empty answer is an ANSWER, not a failure: the chain reads it as "this
       source has nothing" and carries on to the next one. */
    return capped(req, []);
  }

  function typeKey(value) { return String(value || '').trim().toLowerCase(); }

  /* `anchorLeads` is false when the page has just told this shopper the product
     is wrong for their type. 04 §9.8.3: the anchor is then dropped and the
     bundle stands on the approved candidates alone, under the heading the
     regimen path already uses for the same situation. Its category is not
     reserved either -- an approved moisturiser is exactly what belongs beside
     a moisturiser this shopper should not buy, the same reasoning
     regimenCandidates() applies. The approval filter in take() is untouched:
     dropping the anchor lets nothing barred in behind it. */
  function fromPool(root, pool, anchor, anchorLeads, need, type, subtypes) {
    var seen = {};
    seen[anchor.productId] = true;
    /* One product per category, the leading anchor's own reserved. A bundle
       that puts a second cleanser beside a cleanser is not "bought together".
       The Liquid pool already dedupes the brand list this way; the
       recommendation sets are deduped here, where their product type arrives.
       A candidate that states no type is not held against any category. */
    var taken = {};
    var anchorCat = anchorLeads ? typeKey(anchor.type) : '';
    if (anchorCat) taken[anchorCat] = true;
    var picked = [];
    function take(list) {
      list.forEach(function (item) {
        if (picked.length >= need || seen[item.productId] || !buyable(item)) return;
        var cat = typeKey(item.type);
        if (cat && taken[cat]) return;
        /* Typed: approved for their type, explicitly. Untyped: anything buyable. */
        if (type && verdict(item, type, subtypes) !== 'approved') return;
        seen[item.productId] = true;
        if (cat) taken[cat] = true;
        picked.push(item);
      });
    }
    function bySource(name) { return pool.filter(function (item) { return item.source === name; }); }
    /* Not asked for when the bundle is already full: a request nobody reads
       still costs the shopper. */
    function fill(intent) {
      if (picked.length >= need) return Promise.resolve();
      return recommend(root, intent, need + 3).then(take);
    }

    take(bySource('curated'));
    return fill('complementary')
      .then(function () { take(bySource('brand')); return fill('related'); })
      .then(function () {
        if (!picked.length) return null;
        var sources = [];
        picked.forEach(function (item) { if (sources.indexOf(item.source) < 0) sources.push(item.source); });
        return {
          items: (anchorLeads ? [anchor] : []).concat(picked),
          source: sources.join('+'),
          heading: anchorLeads ? null : 'Goes with your ' + type + ' routine'
        };
      });
  }

  // ------------------------------------------------------------------ compose

  function compose(root) {
    var s = state();
    var type = s.type && TYPES.indexOf(s.type) > -1 ? s.type : null;
    var subtypes = type ? wantedSubtypes() : [];
    var max = Math.min(4, Math.max(2, parseInt(root.getAttribute('data-max'), 10) || 3));
    var need = max - 1;

    var poolTpl = qs(root, 'template[data-sts-bundle-pool]');
    var pool = poolTpl ? qsa(poolTpl.content, '[data-sts-bundle-item]').map(function (n) { return readItem(n); }) : [];
    var anchor = null;
    pool.forEach(function (item) { if (item.source === 'anchor') anchor = item; });
    var anchorBarred = buyable(anchor) && wrongForType(anchor, type, subtypes);
    var anchorLeads = buyable(anchor) && !anchorBarred;

    var regimen = type ? loadRegimen(root, s) : Promise.resolve(null);
    return regimen
      .then(function (steps) {
        return steps ? fromRegimen(root, steps, anchor, anchorLeads, need, type, subtypes) : null;
      })
      .then(function (plan) {
        if (plan) return plan;
        /* A barred anchor no longer sinks the row (04 §9.8.3): it is left out
           and the approved candidates are shown as what goes with the routine.
           An anchor that is merely unbuyable is a different thing -- nothing
           to sell and nothing to say about fit -- and still hides the row. */
        if (!anchorLeads && !anchorBarred) return null;
        return fromPool(root, pool, anchor, anchorLeads, need, type, subtypes);
      })
      .then(function (plan) {
        if (!plan || plan.items.length < 2) { hide(root); return; }
        render(root, plan, type, subtypes);
      })
      .catch(function () { hide(root); });
  }

  function hide(root) {
    root.setAttribute('data-state', 'empty');
    root.hidden = true;
  }

  // ------------------------------------------------------------------- render

  /* The live discount's percentage (theme setting), else 10 in the simulator
     only, else 0: no claim. */
  function bundlePct(root) {
    var n = parseInt(root.getAttribute('data-bundle-pct'), 10) || 0;
    var f = window.STS && STS.state && STS.state.flags;
    return n > 0 ? n : (f && f.preview ? 10 : 0);
  }

  function render(root, plan, type, subtypes) {
    var money = moneyFormatter(root);
    var list = qs(root, '[data-sts-bundle-list]');
    var tpl = qs(root, 'template[data-sts-bundle-tpl]');
    if (!list || !tpl || !tpl.content.firstElementChild) { hide(root); return; }

    list.textContent = '';
    var total = 0;
    var pct = bundlePct(root);
    var cut = 0;
    plan.items.forEach(function (item) {
      total += item.price;
      cut += Math.round(item.price * pct / 100);
      var li = tpl.content.firstElementChild.cloneNode(true);
      var href = safeHref(item.url);
      qsa(li, '[data-f="link"]').forEach(function (a) { if (href) a.setAttribute('href', href); });
      var img = qs(li, '[data-f="img"]');
      var src = safeSrc(item.image);
      if (src && img) img.setAttribute('src', src);
      else if (img) img.parentNode.removeChild(img);
      setText(qs(li, '[data-f="brand"]'), item.vendor);
      setText(qs(li, '[data-f="name"]'), item.title);
      setText(qs(li, '[data-f="price"]'), money(item.price));
      /* v6.1 1.5b: the star row. bv.js watches the page and fills a container
         added after it loaded, so this only names the product; it stays empty
         for a product with no reviews. */
      var st = qs(li, '[data-f="stars"]');
      if (st && item.productId) {
        var bv = document.createElement('div');
        bv.setAttribute('data-bv-show', 'inline_rating');
        bv.setAttribute('data-bv-product-id', item.productId);
        bv.setAttribute('data-bv-seo', 'false');
        st.appendChild(bv);
        st.hidden = false;
      }
      var tag = qs(li, '[data-f="tag"]');
      if (tag && item.inRoutine && item.source !== 'anchor') { tag.textContent = 'In your routine'; tag.setAttribute('data-kind', 'routine'); tag.hidden = false; }
      list.appendChild(li);
    });

    var n = plan.items.length;
    var count = COUNT[n] || ['all ' + n, 'All ' + n];

    if (plan.heading) qs(root, '[data-sts-bundle-heading]').textContent = plan.heading;
    /* A routine bundle says whose it is; the saving is claimed only with a
       live discount (bundlePct). The prototype's v6.2 sentences. */
    var sub = qs(root, '[data-sts-bundle-sub]');
    var rest = /^The rest/.test(plan.heading || '');
    if (sub && plan.source === 'regimen') {
      sub.textContent = rest ? 'These are the other steps Dr. Baumann put in your ' + type + ' routine.' : 'Your ' + type + ' routine already has these steps.';
    }
    if (sub && pct) {
      sub.textContent += plan.source !== 'regimen' ? ' Taking the bundle saves ' + pct + '%.'
        : rest || plan.items[0].source !== 'anchor' ? ' Buying them together saves ' + pct + '%.'
          : ' Add this product alongside them and save ' + pct + '%.';
    }

    /* The note is a claim, so it is only made when every product earns it. */
    var note = '';
    if (type) {
      var allRight = plan.items.every(function (item) {
        return item.inRoutine || verdict(item, type, subtypes) === 'approved';
      });
      if (allRight) note = count[1] + ' are right for skin type ' + type;
    } else {
      var mostRight = plan.items.every(function (item) { return item.approved.length >= MOST_TYPES; });
      if (mostRight) note = count[1] + ' are right for most skin types';
    }
    var noteEl = qs(root, '[data-sts-bundle-note]');
    if (noteEl) { noteEl.textContent = note; noteEl.hidden = !note; }

    qs(root, '[data-sts-bundle-sumlabel]').textContent = pct ? 'Bundle of ' + n : 'Total for ' + count[0];
    qs(root, '[data-sts-bundle-total]').textContent = money(total - cut);
    var was = qs(root, '[data-sts-bundle-was]');
    if (was) { was.textContent = cut ? money(total) : ''; was.hidden = !cut; }
    var button = qs(root, '[data-sts-bundle-add]');
    button.textContent = 'Add ' + count[0] + (pct ? ' · save ' + pct + '%' : ' to cart');

    /* v6.2 §7: the bundle's key, the same for the same products in any order. */
    var key = plan.items.map(function (item) { return item.handle; }).sort().join('+');
    root.__stsPlan = { items: plan.items, source: plan.source, total: total, key: key };
    root.setAttribute('data-count', String(n));
    root.setAttribute('data-state', 'ready');
    root.hidden = false;

    if (!button.__stsBound) {
      button.__stsBound = true;
      button.addEventListener('click', function () { addBundle(root, button); });
    }
    affordance(root);
  }

  // --------------------------------------------------------- mobile affordance

  /* On a phone the row scrolls sideways with the next product peeking in. Two
     more cues, because a peek alone is easy to miss at the bottom of a long
     page: the edge that has more content fades out, and a dot per product
     fills while that product is in view. Desktop never scrolls, and there both
     cues switch themselves off (data-scroll="none"). */
  function affordance(root) {
    var list = qs(root, '[data-sts-bundle-list]');
    var host = qs(root, '[data-sts-bundle-dots]');
    if (!list || !host) return;
    var items = qsa(list, '.sts-bundle__item');
    host.textContent = '';
    var dots = items.map(function () {
      var dot = document.createElement('span');
      host.appendChild(dot);
      return dot;
    });

    var frame = 0;
    function sync() {
      frame = 0;
      var max = list.scrollWidth - list.clientWidth;
      var left = Math.abs(list.scrollLeft); /* RTL reports negative offsets */
      var edge = max <= 1 ? 'none' : left <= 1 ? 'start' : left >= max - 1 ? 'end' : 'middle';
      root.setAttribute('data-scroll', edge);
      if (edge === 'none') return;
      var view = list.getBoundingClientRect();
      items.forEach(function (item, i) {
        var box = item.getBoundingClientRect();
        var shown = Math.min(box.right, view.right) - Math.max(box.left, view.left);
        dots[i].classList.toggle('is-on', box.width > 0 && shown / box.width >= 0.6);
      });
    }
    function queue() { if (!frame) frame = window.requestAnimationFrame(sync); }

    if (!list.__stsScrollBound) {
      list.__stsScrollBound = true;
      list.addEventListener('scroll', queue, { passive: true });
      if ('ResizeObserver' in window) new window.ResizeObserver(queue).observe(list);
      else window.addEventListener('resize', queue);
    }
    sync();
  }

  // ---------------------------------------------------------------------- cart

  /* `sts:cart:updated` is the theme's canonical "the cart was written" event
     (assets/sts-routine-shared.js names it) and `theme:cart:change` the legacy
     alias the theme's header count listens on (assets/theme.js). Both carry the
     cart in detail.cart. Same integration as assets/sts-product.js. */
  function announceCart(root) {
    netFetch(rootUrl(root) + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var detail = { cart: cart };
        document.dispatchEvent(new CustomEvent('sts:cart:updated', { detail: detail }));
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: detail }));
      })
      .catch(noop);
  }

  /* The line under the button, never a toast (v5.1 §1.5). Only a failure is
     said; an empty message clears the line. */
  function say(root, message, tone) {
    var line = qs(root, '[data-sts-bundle-status]');
    if (!line) return;
    /* The markup already carries role="status". Only an error escalates, and it
       does so a frame before the text so the region is live when it changes. */
    if (tone === 'error') line.setAttribute('role', 'alert');
    else if (line.getAttribute('role') !== 'status') line.setAttribute('role', 'status');
    if (window.requestAnimationFrame) window.requestAnimationFrame(function () { line.textContent = message; });
    else line.textContent = message;
  }

  function track(root, plan, n, charged) {
    try {
      if (typeof window.stsTrack !== 'function') return;
      window.stsTrack('sts_fbt_add', {
        source: plan.source,
        items: n,
        value: Math.round(charged) / 100,
        currency: root.getAttribute('data-currency') || 'USD'
      });
    } catch (e) { /* analytics never breaks a cart add */ }
  }

  /* A bundle is one multi-line add.js; on a slow phone it outlives the 6 s
     "network is gone" deadline while Shopify is still taking it. More time per
     line, and when the wait still runs out (no answer from Shopify) the cart is
     read before anything is called a failure: every bundle line there means
     the add landed. */
  function addTimeout(lines) {
    return Math.min(30000, Math.max(NET_TIMEOUT_MS, 6000 + 2000 * lines));
  }

  function bundleLanded(root, plan, id) {
    return netFetch(rootUrl(root) + 'cart.js', { headers: { Accept: 'application/json' } }, 10000)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var have = {};
        ((cart && cart.items) || []).forEach(function (line) {
          if (line && line.properties && line.properties._bundle === id) have[String(line.id)] = true;
        });
        return plan.items.every(function (item) { return have[String(item.variantId)]; });
      }, function () { return false; });
  }

  function addBundle(root, button) {
    var plan = root.__stsPlan;
    if (!plan || button.getAttribute('aria-busy') === 'true') return;
    var id = plan.key;
    var n = plan.items.length;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    netFetch(rootUrl(root) + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: plan.items.map(function (item) {
          return { id: Number(item.variantId), quantity: 1, properties: { _bundle: id, _sts_src: 'fbt' } };
        })
      })
    }, addTimeout(n))
      .then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          if (!r.ok) {
            var e = new Error((body && (body.description || body.message)) || 'Please try again.');
            e.description = body && body.description;  // what STS.shopperError shows
            e.answered = r.status < 500;
            throw e;
          }
          return body;
        });
      })
      .catch(function (err) {
        if (err && err.answered) throw err;
        return bundleLanded(root, plan, id).then(function (ok) {
          if (ok) return null;  // landed; the charged total falls back to the plan's
          throw err;
        });
      })
      .then(function (body) {
        /* What the cart charges for the one of each just added — found by the
           bundle key, so a regular line of the same product is not counted. A
           repeat add answers with the merged line, so it is one unit's price,
           never the line's. */
        var charged = 0;
        var matched = 0;
        ((body && body.items) || []).forEach(function (line) {
          if (!line || !line.properties || line.properties._bundle !== id) return;
          matched++;
          charged += typeof line.final_price === 'number' ? line.final_price : (line.price || 0);
        });
        if (!matched) charged = plan.total;
        announceCart(root);
        say(root, '');
        track(root, plan, n, charged);
      })
      .catch(function (err) {
        /* Shopify's reason or a plain line, never the raw exception (STS.shopperError). */
        var f = (window.STS || {}).shopperError;
        say(root, 'We could not add the bundle. ' + (f ? f(err) : 'Please try again.'), 'error');
      })
      .then(function () {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      });
  }

  // ---------------------------------------------------------------------- boot

  /* The row sits below the fold on every viewport, so the routine read and the
     recommendations request wait until it is 600px away. A pending section
     has no height, and a zero-area target still reports intersection. */
  function init(root) {
    if (root.__stsBundle) return;
    root.__stsBundle = true;
    if (!('IntersectionObserver' in window)) { compose(root); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          compose(root);
          return;
        }
      }
    }, { rootMargin: '600px 0px' });
    io.observe(root);
  }

  function boot(scope) {
    qsa(scope || document, '[data-sts-bundle]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  /* Theme editor: a re-rendered section arrives with fresh, unwired markup. */
  document.addEventListener('shopify:section:load', function (ev) { boot(ev.target); });
}());
